import React from 'react';
import { FaSave, FaTimes, FaSync } from 'react-icons/fa';
import InputProveedor from '../util/Inputs/InputProveedor';
import NumericInput from './NumericInput';
import {
  CONCEPTOS,
  ITBIS_OPTIONS,
  ITBIS_RETENIDO_OPTIONS,
  FORMAS_PAGO,
  TIPOS_RETENCION,
  TIPOS_FACTURA,
  validarFormatoNCF,
  autoRellenarNCF
} from './Funciones/FuncionesProcesoCostosGastos';

/**
 * Formulario para captura/edición de detalle de gasto
 * @param {Object} detalle - Objeto con datos de la línea
 * @param {Function} onChange - Callback cuando cambia un campo
 * @param {Function} onGuardar - Callback al guardar
 * @param {Function} onCancelar - Callback al cancelar
 * @param {Function} onRecalcular - Callback para recalcular valores automáticos
 * @param {Function} onAbrirModalProveedor - Callback para abrir modal de selección de proveedor
 * @param {Function} onLimpiarProveedor - Callback para limpiar proveedor seleccionado
 * @param {String} modo - 'nuevo' | 'editando'
 * @param {Number} lineaNumero - Número de línea si está editando
 */
export default function FormularioDetalleGasto({
  detalle,
  onChange,
  onGuardar,
  onCancelar,
  onRecalcular,
  onAbrirModalProveedor,
  onLimpiarProveedor,
  modo = 'nuevo',
  lineaNumero = null
}) {
  
  const handleChange = (campo, valor) => {
    onChange(campo, valor);
  };

  const handleKeyDown = (e) => {
    // Ctrl+Enter para guardar
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      onGuardar();
    }
    // Escape para cancelar
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancelar();
    }
  };

  // Validación visual de NCF
  const validacionNCF = detalle.ncf ? validarFormatoNCF(detalle.ncf) : { isValid: null, error: '' };
  
  // Validación visual de NCF Modificado
  const validacionNCFModificado = detalle.ncf_modificado ? validarFormatoNCF(detalle.ncf_modificado) : { isValid: null, error: '' };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border-2 border-blue-200">
      {/* Header del formulario */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {modo === 'nuevo' ? (
            <>
              <span className="text-2xl">🆕</span>
              <h3 className="text-lg font-bold text-gray-800">Nueva Línea de Gasto</h3>
            </>
          ) : (
            <>
              <span className="text-2xl">✏️</span>
              <h3 className="text-lg font-bold text-gray-800">
                Editando Línea #{lineaNumero}
              </h3>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRecalcular}
            className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
            title="Recalcular valores automáticos"
          >
            <FaSync size={14} />
            Recalcular
          </button>
        </div>
      </div>

      {/* SECCIÓN 1: Datos de la Factura */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">📄 Datos de la Factura</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Mes (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mes
            </label>
            <input
              type="text"
              value={detalle.mes}
              readOnly
              tabIndex={-1}
              className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed"
            />
          </div>

          {/* Día */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Día <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={detalle.dia}
              onChange={(e) => handleChange('dia', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="1-31"
              min="1"
              max="31"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* NCF */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NCF <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={detalle.ncf}
              onChange={(e) => handleChange('ncf', e.target.value)}
              onBlur={(e) => {
                const ncfAutoRellenado = autoRellenarNCF(e.target.value);
                if (ncfAutoRellenado !== e.target.value) {
                  handleChange('ncf', ncfAutoRellenado);
                }
              }}
              onKeyDown={(e) => {
                handleKeyDown(e);
                if (e.key === 'Enter' && !e.ctrlKey) {
                  const ncfAutoRellenado = autoRellenarNCF(e.target.value);
                  if (ncfAutoRellenado !== e.target.value) {
                    handleChange('ncf', ncfAutoRellenado);
                  }
                }
              }}
              placeholder="B0113 o B0100000013"
              maxLength="13"
              className={`w-full px-3 py-2 text-sm rounded-lg focus:ring-2 transition-all ${
                detalle.ncf
                  ? validacionNCF.isValid
                    ? 'border-2 border-green-400 bg-green-50 focus:ring-green-500'
                    : 'border-2 border-red-400 bg-red-50 focus:ring-red-500'
                  : 'border border-gray-300 focus:ring-blue-500'
              }`}
              title={detalle.ncf ? (validacionNCF.isValid ? `✓ NCF válido (${validacionNCF.tipo})` : `✗ ${validacionNCF.error}`) : 'Ingrese formato corto (ej: B0113) o completo. Auto-rellena con ceros al salir del campo'}
            />
          </div>

          {/* NCF Modificado */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NCF Modificado
            </label>
            <input
              type="text"
              value={detalle.ncf_modificado}
              onChange={(e) => handleChange('ncf_modificado', e.target.value)}
              onBlur={(e) => {
                const ncfAutoRellenado = autoRellenarNCF(e.target.value);
                if (ncfAutoRellenado !== e.target.value) {
                  handleChange('ncf_modificado', ncfAutoRellenado);
                }
              }}
              onKeyDown={(e) => {
                handleKeyDown(e);
                if (e.key === 'Enter' && !e.ctrlKey) {
                  const ncfAutoRellenado = autoRellenarNCF(e.target.value);
                  if (ncfAutoRellenado !== e.target.value) {
                    handleChange('ncf_modificado', ncfAutoRellenado);
                  }
                }
              }}
              placeholder="Opcional (ej: B0113)"
              maxLength="13"
              className={`w-full px-3 py-2 text-sm rounded-lg focus:ring-2 transition-all ${
                detalle.ncf_modificado
                  ? validacionNCFModificado.isValid
                    ? 'border-2 border-green-400 bg-green-50 focus:ring-green-500'
                    : 'border-2 border-red-400 bg-red-50 focus:ring-red-500'
                  : 'border border-gray-300 focus:ring-blue-500'
              }`}
              title={detalle.ncf_modificado ? (validacionNCFModificado.isValid ? `✓ NCF Modificado válido (${validacionNCFModificado.tipo})` : `✗ ${validacionNCFModificado.error}`) : 'Ingrese formato corto o completo. Auto-rellena con ceros al salir del campo'}
            />
          </div>

          {/* Suplidor */}
          <div className="lg:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suplidor <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onAbrirModalProveedor}
                className="flex-1 px-3 py-2 text-sm text-left border border-gray-300 rounded-lg hover:border-blue-400 focus:ring-2 focus:ring-blue-500 transition-all truncate"
                title={detalle.nombre_proveedor || 'Seleccionar proveedor'}
              >
                {detalle.nombre_proveedor || 'Seleccionar proveedor...'}
              </button>
              {detalle.nombre_proveedor && (
                <button
                  type="button"
                  onClick={onLimpiarProveedor}
                  className="px-3 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                  title="Limpiar proveedor"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            {detalle.rnc_proveedor && (
              <p className="text-xs text-gray-500 mt-1">RNC: {detalle.rnc_proveedor}</p>
            )}
          </div>

          {/* Descripción */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <input
              type="text"
              value={detalle.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Descripción del bien o servicio"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* SECCIÓN 2: Concepto y Clasificación */}
      <div className="mb-4 border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">🏷️ Concepto y Clasificación</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Concepto */}
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Concepto <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              list="conceptos-list"
              value={CONCEPTOS.find(c => c.value === detalle.concepto)?.label || detalle.concepto || ''}
              onChange={(e) => {
                const conceptoEncontrado = CONCEPTOS.find(c => 
                  c.label.toLowerCase() === e.target.value.toLowerCase()
                );
                handleChange('concepto', conceptoEncontrado ? conceptoEncontrado.value : e.target.value);
              }}
              onBlur={(e) => {
                const conceptoEncontrado = CONCEPTOS.find(c => 
                  c.label.toLowerCase() === e.target.value.toLowerCase()
                );
                if (conceptoEncontrado) {
                  handleChange('concepto', conceptoEncontrado.value);
                } else if (e.target.value === '') {
                  handleChange('concepto', '');
                }
              }}
              onClick={(e) => {
                // Seleccionar todo el texto para ver opciones del datalist
                e.target.select();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Escriba o seleccione concepto..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              title="Seleccionar concepto auto-rellena otros campos. Haga clic para ver todas las opciones"
            />
            <datalist id="conceptos-list">
              {CONCEPTOS.map(concepto => (
                <option key={concepto.value} value={concepto.label} />
              ))}
            </datalist>
          </div>

          {/* Tipo de Gasto 606 */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Gasto 606 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={detalle.tipo_gasto_606}
              onChange={(e) => handleChange('tipo_gasto_606', e.target.value)}
              tabIndex={-1}
              className="w-full px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Auto-llenado"
            />
          </div>

          {/* Tipo Factura */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo Factura
            </label>
            <select
              value={detalle.tipo_factura}
              onChange={(e) => handleChange('tipo_factura', e.target.value)}
              tabIndex={-1}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-green-50"
            >
              {TIPOS_FACTURA.map(tipo => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SECCIÓN 3: Montos Base */}
      <div className="mb-4 border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">💰 Montos Base</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Monto */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto <span className="text-red-500">*</span>
            </label>
            <NumericInput
              value={detalle.monto}
              onChange={(valor) => handleChange('monto', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Descuento */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descuento
            </label>
            <NumericInput
              value={detalle.descuento}
              onChange={(valor) => handleChange('descuento', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Monto Facturado */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto Facturado
            </label>
            <NumericInput
              value={detalle.monto_facturado}
              onChange={(valor) => handleChange('monto_facturado', valor)}
              tabIndex={-1}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right bg-yellow-50 border border-yellow-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              title="Monto - Descuento (auto-calculado)"
            />
          </div>
        </div>
      </div>

      {/* SECCIÓN 4: ITBIS */}
      <div className="mb-4 border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">📊 ITBIS</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* ITBIS % */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS %
            </label>
            <select
              value={detalle.itbis_porcentaje}
              onChange={(e) => {
                const valor = e.target.value === 'exento' ? 'exento' : parseInt(e.target.value);
                handleChange('itbis_porcentaje', valor);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {ITBIS_OPTIONS.map(opcion => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </select>
          </div>

          {/* ITBIS Facturado - Auto-calculado si hay %, editable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS Facturado <span className="text-red-500">*</span>
            </label>
            <NumericInput
              value={detalle.itbis_facturado}
              onChange={(valor) => handleChange('itbis_facturado', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right bg-blue-50 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              title="Auto-calculado según ITBIS %, pero puede editarse manualmente"
            />
          </div>

          {/* ITBIS Retenido % */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS Ret. %
            </label>
            <select
              value={detalle.itbis_retenido_porcentaje}
              onChange={(e) => handleChange('itbis_retenido_porcentaje', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {ITBIS_RETENIDO_OPTIONS.map(opcion => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </select>
          </div>

          {/* ITBIS Retenido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS Retenido
            </label>
            <NumericInput
              value={detalle.itbis_retenido}
              onChange={(valor) => handleChange('itbis_retenido', valor)}
              tabIndex={-1}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right bg-yellow-50 border border-yellow-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              title="Auto-calculado, editable"
            />
          </div>

          {/* ITBIS Proporcionalidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS Prop.
            </label>
            <input
              type="number"
              value={detalle.itbis_proporcionalidad}
              readOnly
              tabIndex={-1}
              className="w-full px-3 py-2 text-sm text-right bg-blue-50 border border-blue-300 rounded-lg cursor-not-allowed"
              title="ITBIS sujeto a Proporcionalidad (Art. 349)"
            />
          </div>

          {/* ITBIS Costo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ITBIS Costo
            </label>
            <input
              type="number"
              value={detalle.itbis_costo}
              readOnly
              tabIndex={-1}
              className="w-full px-3 py-2 text-sm text-right bg-blue-50 border border-blue-300 rounded-lg cursor-not-allowed"
              title="ITBIS llevado al Costo"
            />
          </div>
        </div>
      </div>

      {/* SECCIÓN 5: ISR y Otros Impuestos */}
      <div className="mb-4 border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">💼 ISR y Otros Impuestos</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* ISR Retenido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ISR Retenido
            </label>
            <NumericInput
              value={detalle.isr_retenido}
              onChange={(valor) => handleChange('isr_retenido', valor)}
              tabIndex={-1}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              disabled={detalle.tipo_factura === 'Bienes'}
              className="w-full px-3 py-2 text-sm text-right bg-yellow-50 border border-yellow-200 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              title={detalle.tipo_factura === 'Bienes' ? 'ISR no aplica para bienes' : 'Auto-calculado, editable'}
            />
          </div>

          {/* Tipo Retención */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo Retención
            </label>
            <select
              value={detalle.tipo_retencion || ''}
              onChange={(e) => handleChange('tipo_retencion', e.target.value)}
              disabled={detalle.tipo_factura === 'Bienes'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {TIPOS_RETENCION.map(tipo => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>

          {/* Impuesto Selectivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Imp. Selectivo
            </label>
            <NumericInput
              value={detalle.impuesto_selectivo}
              onChange={(valor) => handleChange('impuesto_selectivo', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Otros Impuestos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Otros Imp.
            </label>
            <NumericInput
              value={detalle.otros_impuestos}
              onChange={(valor) => handleChange('otros_impuestos', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Propina Legal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Propina Legal
            </label>
            <NumericInput
              value={detalle.propina_legal}
              onChange={(valor) => handleChange('propina_legal', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Otros Cargos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Otros Cargos
            </label>
            <NumericInput
              value={detalle.otros_cargos}
              onChange={(valor) => handleChange('otros_cargos', valor)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* SECCIÓN 6: Total y Forma de Pago */}
      <div className="mb-4 border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 border-b pb-2">💳 Total y Forma de Pago</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Total Facturado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Facturado
            </label>
            <NumericInput
              value={detalle.total_facturado}
              onChange={(valor) => handleChange('total_facturado', valor)}
              tabIndex={-1}
              placeholder="0.00"
              step={0.01}
              min={0}
              decimals={2}
              className="w-full px-3 py-2 text-sm text-right bg-yellow-50 border border-yellow-200 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
              title="Auto-calculado, editable"
            />
          </div>

          {/* Forma de Pago */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Forma de Pago <span className="text-red-500">*</span>
            </label>
            <select
              value={detalle.forma_pago || ''}
              onChange={(e) => handleChange('forma_pago', e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              title="Puede escribir para buscar por código o descripción"
            >
              {FORMAS_PAGO.map(forma => (
                <option key={forma.value} value={forma.value}>
                  {forma.label}
                </option>
              ))}
            </select>
          </div>

          {/* Número de Cheque */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Núm. Cheque
            </label>
            <input
              type="text"
              value={detalle.numero_cheque || ''}
              onChange={(e) => handleChange('numero_cheque', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder=""
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fecha Cheque */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Cheque
            </label>
            <input
              type="date"
              value={detalle.fecha_cheque || ''}
              onChange={(e) => handleChange('fecha_cheque', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Banco */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banco
            </label>
            <input
              type="text"
              value={detalle.banco || ''}
              onChange={(e) => handleChange('banco', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder=""
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Cuenta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cuenta
            </label>
            <input
              type="text"
              value={detalle.cuenta || ''}
              onChange={(e) => handleChange('cuenta', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder=""
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          <kbd className="px-2 py-1 bg-gray-100 rounded border">Ctrl+Enter</kbd> para guardar • 
          <kbd className="px-2 py-1 bg-gray-100 rounded border ml-1">Esc</kbd> para cancelar
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancelar}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all flex items-center gap-2"
          >
            <FaTimes />
            Cancelar
          </button>
          <button
            type="button"
            onClick={onGuardar}
            className="px-6 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
          >
            <FaSave />
            {modo === 'nuevo' ? 'Guardar Línea' : 'Actualizar Línea'}
          </button>
        </div>
      </div>
    </div>
  );
}
